import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import Modal from '../../../components/ui/Modal'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import Table, { Pagination } from '../../../components/ui/Table'
import Badge from '../../../components/ui/Badge'
import api from '../../../lib/api'
import toast from 'react-hot-toast'

interface PincodeEntry {
  id: string
  pincode: string
  state: string
  state_code: string
  district: string
  city: string
  localities: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

interface ListResponse {
  data: PincodeEntry[]
  states: string[]
  meta: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export default function AddressMasterPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [editingEntry, setEditingEntry] = useState<PincodeEntry | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ['address-master', page, search, stateFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), per_page: '50' })
      if (search) params.append('search', search)
      if (stateFilter) params.append('state', stateFilter)
      const { data } = await api.get(`/admin/address?${params}`)
      return data
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (pincode: string) => {
      await api.delete(`/admin/address/pincode/${pincode}`)
    },
    onSuccess: () => {
      toast.success('Pincode deactivated')
      queryClient.invalidateQueries({ queryKey: ['address-master'] })
    },
    onError: () => {
      toast.error('Failed to deactivate pincode')
    },
  })

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post('/admin/address/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: (data: { imported: number; skipped: number }) => {
      toast.success(`Imported ${data.imported} pincodes, skipped ${data.skipped}`)
      queryClient.invalidateQueries({ queryKey: ['address-master'] })
      setShowImportModal(false)
    },
    onError: () => {
      toast.error('Failed to import CSV')
    },
  })

  const handleImport = () => {
    const file = fileInputRef.current?.files?.[0]
    if (file) {
      importMutation.mutate(file)
    }
  }

  const stateOptions = (data?.states || []).map(s => ({ value: s, label: s }))

  const columns = [
    {
      key: 'pincode',
      header: 'PIN Code',
      render: (entry: PincodeEntry) => (
        <span className="font-mono font-medium text-gray-900 dark:text-white">{entry.pincode}</span>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (entry: PincodeEntry) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{entry.city}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {entry.district && entry.district !== entry.city ? `${entry.district}, ` : ''}
            {entry.state}
          </p>
        </div>
      ),
    },
    {
      key: 'state_code',
      header: 'State Code',
      render: (entry: PincodeEntry) => (
        <Badge variant="info">{entry.state_code}</Badge>
      ),
    },
    {
      key: 'localities',
      header: 'Localities',
      render: (entry: PincodeEntry) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {entry.localities?.length || 0} areas
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (entry: PincodeEntry) => (
        <Badge variant={entry.is_active ? 'success' : 'gray'}>
          {entry.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (entry: PincodeEntry) => (
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setEditingEntry(entry)}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            onClick={() => {
              if (confirm('Deactivate this pincode?')) {
                deleteMutation.mutate(entry.pincode)
              }
            }}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Address Master</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage PIN code database for India address auto-fill
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            <i className="fas fa-upload mr-2"></i>
            Import CSV
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <i className="fas fa-plus mr-2"></i>
            Add Pincode
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by PIN code, city, or district..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              icon="fa-search"
            />
          </div>
          <div className="w-full sm:w-48">
            <SearchableSelect
              value={stateFilter}
              onChange={(val) => { setStateFilter(val); setPage(1) }}
              placeholder="All States"
              options={[{ value: '', label: 'All States' }, ...stateOptions]}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.data ?? []}
          loading={isLoading}
          keyExtractor={(entry) => entry.id}
          emptyMessage="No PIN codes found"
        />
        {data && data.meta.total_pages > 1 && (
          <Pagination
            page={page}
            totalPages={data.meta.total_pages}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Stats */}
      {data && (
        <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Showing {data.data.length} of {data.meta.total} PIN codes
          {stateFilter && ` in ${stateFilter}`}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingEntry) && (
        <PincodeModal
          entry={editingEntry}
          onClose={() => { setShowCreateModal(false); setEditingEntry(null) }}
          onSuccess={() => {
            setShowCreateModal(false)
            setEditingEntry(null)
            queryClient.invalidateQueries({ queryKey: ['address-master'] })
          }}
        />
      )}

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import PIN Codes from CSV"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">CSV Format</h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Required columns: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">pincode</code>,{' '}
              <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">state</code>,{' '}
              <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">city</code>
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Optional: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">district</code>,{' '}
              <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">state_code</code>,{' '}
              <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">localities</code> (semicolon-separated)
            </p>
          </div>

          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={() => {}}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-primary-600 hover:text-primary-700"
            >
              <i className="fas fa-cloud-upload-alt text-3xl mb-2"></i>
              <p className="font-medium">Click to select CSV file</p>
            </button>
            {fileInputRef.current?.files?.[0] && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Selected: {fileInputRef.current.files[0].name}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              loading={importMutation.isPending}
              disabled={!fileInputRef.current?.files?.length}
            >
              <i className="fas fa-upload mr-2"></i>
              Import
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// Pincode Create/Edit Modal
interface PincodeModalProps {
  entry: PincodeEntry | null
  onClose: () => void
  onSuccess: () => void
}

function PincodeModal({ entry, onClose, onSuccess }: PincodeModalProps) {
  const isEditing = !!entry
  const [formData, setFormData] = useState({
    pincode: entry?.pincode || '',
    state: entry?.state || '',
    stateCode: entry?.state_code || '',
    district: entry?.district || '',
    city: entry?.city || '',
    localities: entry?.localities?.join(', ') || '',
    isActive: entry?.is_active ?? true,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const localities = data.localities
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)

      if (isEditing) {
        await api.put(`/admin/address/pincode/${entry.pincode}`, {
          state: data.state,
          state_code: data.stateCode,
          district: data.district,
          city: data.city,
          localities,
          is_active: data.isActive,
        })
      } else {
        await api.post('/admin/address/pincode', {
          pincode: data.pincode,
          state: data.state,
          state_code: data.stateCode,
          district: data.district,
          city: data.city,
          localities,
        })
      }
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Pincode updated' : 'Pincode created')
      onSuccess()
    },
    onError: () => {
      toast.error('Failed to save pincode')
    },
  })

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!isEditing && formData.pincode.length !== 6) {
      newErrors.pincode = 'PIN code must be 6 digits'
    }
    if (!formData.state.trim()) newErrors.state = 'State is required'
    if (!formData.city.trim()) newErrors.city = 'City is required'
    if (!formData.stateCode || formData.stateCode.length !== 2) {
      newErrors.stateCode = 'State code must be 2 characters'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      mutation.mutate(formData)
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEditing ? 'Edit Pincode' : 'Add New Pincode'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="PIN Code *"
            value={formData.pincode}
            onChange={(e) => setFormData({ ...formData, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
            error={errors.pincode}
            placeholder="400001"
            disabled={isEditing}
            maxLength={6}
          />
          <Input
            label="State Code *"
            value={formData.stateCode}
            onChange={(e) => setFormData({ ...formData, stateCode: e.target.value.toUpperCase().slice(0, 2) })}
            error={errors.stateCode}
            placeholder="MH"
            maxLength={2}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="State *"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            error={errors.state}
            placeholder="Maharashtra"
          />
          <Input
            label="City *"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            error={errors.city}
            placeholder="Mumbai"
          />
        </div>

        <Input
          label="District"
          value={formData.district}
          onChange={(e) => setFormData({ ...formData, district: e.target.value })}
          placeholder="Mumbai Suburban"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Localities (comma-separated)
          </label>
          <textarea
            value={formData.localities}
            onChange={(e) => setFormData({ ...formData, localities: e.target.value })}
            placeholder="Andheri, Bandra, Juhu"
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {isEditing && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className={`fas fa-${isEditing ? 'save' : 'plus'} mr-2`}></i>
            {isEditing ? 'Save Changes' : 'Add Pincode'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
